import { useState, useEffect } from 'react';

interface StyleSelectorProps {
  selectedStyle: string;
  onSelectStyle: (style: string) => void;
  onPaletteChange?: (styleName: string, palette: ColorPalette) => void;
  darkMode: boolean;
}

interface StyleHistoryItem {
  name: string;
  image: string;
  category: string;
  timestamp: number;
}

interface ColorPalette {
  wall: string;
  roof: string;
  trim: string;
}

export default function StyleSelector({ selectedStyle, onSelectStyle, onPaletteChange, darkMode }: StyleSelectorProps) {
  const [compareMode, setCompareMode] = useState(false);
  const [styleA, setStyleA] = useState<string>('');
  const [styleB, setStyleB] = useState<string>('');
  const [favorites, setFavorites] = useState<string[]>([]);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [styleHistory, setStyleHistory] = useState<StyleHistoryItem[]>([]);
  const [expandedPalette, setExpandedPalette] = useState<string | null>(null);
  const [customPalettes, setCustomPalettes] = useState<{ [styleName: string]: ColorPalette }>({});

  // Load favorites from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('masterArchitectFavorites');
    if (saved) {
      try {
        setFavorites(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse favorites:', e);
      }
    }
  }, []);

  // Load style history from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('masterArchitectStyleHistory');
    if (saved) {
      try {
        setStyleHistory(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse style history:', e);
      }
    }
  }, []);

  // Load custom palettes from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('masterArchitectColorPalettes');
    if (saved) {
      try {
        setCustomPalettes(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse color palettes:', e);
      }
    }
  }, []);

  // Save favorites to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('masterArchitectFavorites', JSON.stringify(favorites));
  }, [favorites]);

  // Save style history to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('masterArchitectStyleHistory', JSON.stringify(styleHistory));
  }, [styleHistory]);

  // Save custom palettes to localStorage
  useEffect(() => {
    localStorage.setItem('masterArchitectColorPalettes', JSON.stringify(customPalettes));
  }, [customPalettes]);

  // Predefined color palettes for each architectural style
  const predefinedPalettes: { [styleName: string]: ColorPalette[] } = {
    'Colonial': [
      { wall: '#F5F5DC', roof: '#8B4513', trim: '#FFFFFF' },
      { wall: '#FFFFFF', roof: '#2F4F4F', trim: '#000080' },
      { wall: '#FFE4B5', roof: '#A0522D', trim: '#8B4513' }
    ],
    'Victorian': [
      { wall: '#E6E6FA', roof: '#4B0082', trim: '#FFD700' },
      { wall: '#FFB6C1', roof: '#8B008B', trim: '#FFFFFF' },
      { wall: '#F0E68C', roof: '#556B2F', trim: '#8B4513' }
    ],
    'Craftsman': [
      { wall: '#D2B48C', roof: '#654321', trim: '#8B4513' },
      { wall: '#DEB887', roof: '#8B4513', trim: '#F5DEB3' },
      { wall: '#BC8F8F', roof: '#A0522D', trim: '#FAEBD7' }
    ],
    'Tudor': [
      { wall: '#F5F5DC', roof: '#8B4513', trim: '#654321' },
      { wall: '#FAEBD7', roof: '#A0522D', trim: '#8B4513' },
      { wall: '#FFE4C4', roof: '#654321', trim: '#D2691E' }
    ],
    'Minimalist': [
      { wall: '#FFFFFF', roof: '#2F4F4F', trim: '#696969' },
      { wall: '#F5F5F5', roof: '#000000', trim: '#DCDCDC' },
      { wall: '#E8E8E8', roof: '#4A4A4A', trim: '#FFFFFF' }
    ],
    'Contemporary': [
      { wall: '#D3D3D3', roof: '#2F4F4F', trim: '#000000' },
      { wall: '#C0C0C0', roof: '#696969', trim: '#FFFFFF' },
      { wall: '#F0F0F0', roof: '#1C1C1C', trim: '#A9A9A9' }
    ],
    'Industrial': [
      { wall: '#808080', roof: '#2F4F4F', trim: '#696969' },
      { wall: '#A9A9A9', roof: '#000000', trim: '#D3D3D3' },
      { wall: '#696969', roof: '#1C1C1C', trim: '#C0C0C0' }
    ],
    'Scandinavian': [
      { wall: '#FFFFFF', roof: '#2F4F4F', trim: '#F5F5DC' },
      { wall: '#F5F5F5', roof: '#696969', trim: '#D2B48C' },
      { wall: '#FAFAFA', roof: '#4A4A4A', trim: '#DEB887' }
    ],
    'Mediterranean': [
      { wall: '#FAEBD7', roof: '#D2691E', trim: '#F5DEB3' },
      { wall: '#FFE4C4', roof: '#CD853F', trim: '#FFDEAD' },
      { wall: '#FFDAB9', roof: '#A0522D', trim: '#FFE4B5' }
    ],
    'Farmhouse': [
      { wall: '#FFFFFF', roof: '#2F4F4F', trim: '#000000' },
      { wall: '#F5F5F5', roof: '#696969', trim: '#1C1C1C' },
      { wall: '#FAFAFA', roof: '#4A4A4A', trim: '#8B4513' }
    ],
    'Ranch': [
      { wall: '#D2B48C', roof: '#8B4513', trim: '#F5DEB3' },
      { wall: '#DEB887', roof: '#A0522D', trim: '#FAEBD7' },
      { wall: '#F5DEB3', roof: '#654321', trim: '#D2691E' }
    ],
    'Cape Cod': [
      { wall: '#F5F5F5', roof: '#2F4F4F', trim: '#FFFFFF' },
      { wall: '#E6E6FA', roof: '#4B0082', trim: '#F0F0F0' },
      { wall: '#FAFAFA', roof: '#696969', trim: '#E8E8E8' }
    ],
    'Tiny Home': [
      { wall: '#D2B48C', roof: '#654321', trim: '#8B4513' },
      { wall: '#FFFFFF', roof: '#2F4F4F', trim: '#696969' },
      { wall: '#F5F5DC', roof: '#8B4513', trim: '#D2691E' }
    ],
    'Container': [
      { wall: '#808080', roof: '#2F4F4F', trim: '#D3D3D3' },
      { wall: '#696969', roof: '#000000', trim: '#C0C0C0' },
      { wall: '#A9A9A9', roof: '#1C1C1C', trim: '#DCDCDC' }
    ],
    'A-Frame': [
      { wall: '#8B4513', roof: '#654321', trim: '#D2691E' },
      { wall: '#A0522D', roof: '#8B4513', trim: '#DEB887' },
      { wall: '#D2B48C', roof: '#654321', trim: '#F5DEB3' }
    ],
    'Dome': [
      { wall: '#FFFFFF', roof: '#C0C0C0', trim: '#696969' },
      { wall: '#F5F5F5', roof: '#A9A9A9', trim: '#2F4F4F' },
      { wall: '#E8E8E8', roof: '#808080', trim: '#4A4A4A' }
    ]
  };

  const getDefaultPalette = (styleName: string): ColorPalette => {
    const palettes = predefinedPalettes[styleName];
    return palettes ? palettes[0] : { wall: '#FFFFFF', roof: '#2F4F4F', trim: '#696969' };
  };

  const getCurrentPalette = (styleName: string): ColorPalette => {
    return customPalettes[styleName] || getDefaultPalette(styleName);
  };

  const handlePaletteChange = (styleName: string, palette: ColorPalette) => {
    setCustomPalettes(prev => ({ ...prev, [styleName]: palette }));
    if (onPaletteChange) {
      onPaletteChange(styleName, palette);
    }
  };

  const handlePresetPaletteSelect = (styleName: string, palette: ColorPalette) => {
    handlePaletteChange(styleName, palette);
  };

  const handleCustomColorChange = (styleName: string, colorType: 'wall' | 'roof' | 'trim', color: string) => {
    const currentPalette = getCurrentPalette(styleName);
    const newPalette = { ...currentPalette, [colorType]: color };
    handlePaletteChange(styleName, newPalette);
  };

  const toggleFavorite = (styleName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setFavorites(prev => 
      prev.includes(styleName)
        ? prev.filter(s => s !== styleName)
        : [...prev, styleName]
    );
  };

  const isFavorite = (styleName: string) => favorites.includes(styleName);

  const styleCategories = [
    {
      category: 'Classic',
      styles: [
        { name: 'Colonial', image: 'https://public.readdy.ai/ai/img_res/3a6824e3a8ce6325611e2c1a19bd5e8d.jpg' },
        { name: 'Victorian', image: 'https://readdy.ai/api/search-image?query=ornate%20victorian%20house%20with%20decorative%20trim%20bay%20windows%20turret%20tower%20pastel%20colors%20intricate%20architectural%20details%20beautiful%20garden%20clear%20sky&width=400&height=300&seq=victorian-style-001&orientation=landscape' },
        { name: 'Craftsman', image: 'https://readdy.ai/api/search-image?query=craftsman%20style%20bungalow%20with%20exposed%20beams%20wide%20front%20porch%20natural%20wood%20siding%20stone%20accents%20warm%20inviting%20architecture%20beautiful%20landscaping&width=400&height=300&seq=craftsman-style-001&orientation=landscape' },
        { name: 'Tudor', image: 'https://readdy.ai/api/search-image?query=tudor%20style%20house%20with%20half%20timbered%20facade%20steeply%20pitched%20roof%20decorative%20woodwork%20brick%20and%20stucco%20exterior%20charming%20english%20architecture%20beautiful%20garden&width=400&height=300&seq=tudor-style-001&orientation=landscape' }
      ]
    },
    {
      category: 'Modern',
      styles: [
        { name: 'Minimalist', image: 'https://readdy.ai/api/search-image?query=minimalist%20modern%20house%20with%20clean%20lines%20white%20walls%20large%20windows%20simple%20geometric%20shapes%20sleek%20contemporary%20architecture%20beautiful%20minimal%20landscaping%20clear%20sky&width=400&height=300&seq=minimalist-style-001&orientation=landscape' },
        { name: 'Contemporary', image: 'https://readdy.ai/api/search-image?query=contemporary%20house%20with%20mixed%20materials%20glass%20steel%20wood%20asymmetrical%20design%20innovative%20architecture%20beautiful%20modern%20landscaping%20clear%20blue%20sky&width=400&height=300&seq=contemporary-style-001&orientation=landscape' },
        { name: 'Industrial', image: 'https://readdy.ai/api/search-image?query=industrial%20style%20house%20with%20exposed%20concrete%20metal%20accents%20large%20windows%20raw%20materials%20urban%20architecture%20beautiful%20minimal%20landscaping&width=400&height=300&seq=industrial-style-001&orientation=landscape' },
        { name: 'Scandinavian', image: 'https://readdy.ai/api/search-image?query=scandinavian%20style%20house%20with%20light%20wood%20facade%20simple%20clean%20design%20large%20windows%20natural%20materials%20nordic%20architecture%20beautiful%20minimal%20garden&width=400&height=300&seq=scandinavian-style-001&orientation=landscape' }
      ]
    },
    {
      category: 'Regional',
      styles: [
        { name: 'Mediterranean', image: 'https://readdy.ai/api/search-image?query=mediterranean%20villa%20with%20terracotta%20roof%20stucco%20walls%20arched%20windows%20courtyard%20warm%20earth%20tones%20beautiful%20landscaping%20palm%20trees%20clear%20sky&width=400&height=300&seq=mediterranean-style-001&orientation=landscape' },
        { name: 'Farmhouse', image: 'https://readdy.ai/api/search-image?query=modern%20farmhouse%20with%20white%20board%20and%20batten%20siding%20black%20windows%20metal%20roof%20front%20porch%20rustic%20charm%20beautiful%20landscaping%20clear%20sky&width=400&height=300&seq=farmhouse-style-001&orientation=landscape' },
        { name: 'Ranch', image: 'https://readdy.ai/api/search-image?query=ranch%20style%20house%20with%20long%20low%20profile%20attached%20garage%20horizontal%20lines%20simple%20design%20beautiful%20landscaping%20wide%20open%20yard%20clear%20sky&width=400&height=300&seq=ranch-style-001&orientation=landscape' },
        { name: 'Cape Cod', image: 'https://readdy.ai/api/search-image?query=cape%20cod%20style%20house%20with%20steep%20roof%20dormer%20windows%20shingle%20siding%20symmetrical%20facade%20traditional%20new%20england%20architecture%20beautiful%20garden&width=400&height=300&seq=capecod-style-001&orientation=landscape' }
      ]
    },
    {
      category: 'Specialty',
      styles: [
        { name: 'Tiny Home', image: 'https://readdy.ai/api/search-image?query=modern%20tiny%20house%20with%20efficient%20design%20large%20windows%20wood%20siding%20compact%20architecture%20beautiful%20minimal%20landscaping%20clear%20sky%20sustainable%20living&width=400&height=300&seq=tinyhome-style-001&orientation=landscape' },
        { name: 'Container', image: 'https://readdy.ai/api/search-image?query=shipping%20container%20house%20with%20industrial%20design%20large%20windows%20modern%20conversion%20innovative%20architecture%20beautiful%20minimal%20landscaping%20clear%20sky&width=400&height=300&seq=container-style-001&orientation=landscape' },
        { name: 'A-Frame', image: 'https://readdy.ai/api/search-image?query=a-frame%20cabin%20with%20steep%20triangular%20roof%20large%20front%20windows%20wood%20siding%20mountain%20retreat%20architecture%20beautiful%20natural%20landscaping%20clear%20sky&width=400&height=300&seq=aframe-style-001&orientation=landscape' },
        { name: 'Dome', image: 'https://readdy.ai/api/search-image?query=geodesic%20dome%20house%20with%20unique%20spherical%20design%20modern%20materials%20innovative%20architecture%20beautiful%20landscaping%20clear%20sky%20futuristic%20sustainable%20home&width=400&height=300&seq=dome-style-001&orientation=landscape' }
      ]
    }
  ];

  // Flatten all styles for easier lookup
  const allStyles = styleCategories.flatMap(cat => 
    cat.styles.map(style => ({
      ...style,
      category: cat.category
    }))
  );

  const getStyleData = (styleName: string) => {
    return allStyles.find(s => s.name === styleName);
  };

  // Add style to history (max 5, newest first, no duplicates)
  const addToHistory = (styleName: string) => {
    const styleData = getStyleData(styleName);
    if (!styleData) return;

    setStyleHistory(prev => {
      // Remove if already exists
      const filtered = prev.filter(item => item.name !== styleName);
      // Add to front
      const newHistory: StyleHistoryItem[] = [
        {
          name: styleData.name,
          image: styleData.image,
          category: styleData.category,
          timestamp: Date.now()
        },
        ...filtered
      ];
      // Keep only last 5
      return newHistory.slice(0, 5);
    });
  };

  // Remove style from history
  const removeFromHistory = (styleName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setStyleHistory(prev => prev.filter(item => item.name !== styleName));
  };

  // Clear all history
  const clearHistory = () => {
    setStyleHistory([]);
  };

  // Wrap onSelectStyle to add to history
  const handleSelectStyle = (styleName: string) => {
    onSelectStyle(styleName);
    addToHistory(styleName);
  };

  // Filter categories based on favorites toggle
  const filteredCategories = showFavoritesOnly
    ? styleCategories.map(cat => ({
        ...cat,
        styles: cat.styles.filter(style => isFavorite(style.name))
      })).filter(cat => cat.styles.length > 0)
    : styleCategories;

  const handleSelectAsMain = (styleName: string) => {
    handleSelectStyle(styleName);
    setCompareMode(false);
    setStyleA('');
    setStyleB('');
  };

  const handleExitCompare = () => {
    setCompareMode(false);
    setStyleA('');
    setStyleB('');
  };

  if (compareMode) {
    return (
      <div className="p-5 space-y-4">
        {/* Header */}
        <div className={`flex items-center justify-between sticky top-0 z-10 py-2 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <div>
            <h3 className={`text-base font-bold transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
              Compare Styles
            </h3>
            <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Pick one style per column, then select your favourite
            </p>
          </div>
          <button
            onClick={handleExitCompare}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-300 whitespace-nowrap flex items-center gap-1.5 cursor-pointer ${
              darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <i className="ri-close-line"></i>Exit
          </button>
        </div>

        {/* Side-by-side preview (shown when both are selected) */}
        {styleA && styleB && getStyleData(styleA) && getStyleData(styleB) && (
          <div className={`rounded-xl border overflow-hidden ${darkMode ? 'border-gray-600 bg-gray-700/40' : 'border-gray-200 bg-gray-50'}`}>
            <div className="grid grid-cols-2">
              {/* Style A preview */}
              <div className="relative">
                <div className="w-full h-36 overflow-hidden">
                  <img src={getStyleData(styleA)!.image} alt={styleA} className="w-full h-full object-cover object-top" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-2 left-2 right-2">
                  <p className="text-white text-xs font-bold truncate">{styleA}</p>
                  <p className="text-white/70 text-xs">{getStyleData(styleA)!.category}</p>
                </div>
                <button
                  onClick={() => handleSelectAsMain(styleA)}
                  className="absolute top-2 right-2 px-2 py-1 bg-teal-500 text-white text-xs font-semibold rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Use This
                </button>
              </div>

              {/* VS divider */}
              <div className="absolute left-1/2 top-0 bottom-0 flex items-center justify-center" style={{ transform: 'translateX(-50%)', zIndex: 10, position: 'relative', width: 0 }}>
              </div>

              {/* Style B preview */}
              <div className="relative border-l-2 border-white/30">
                <div className="w-full h-36 overflow-hidden">
                  <img src={getStyleData(styleB)!.image} alt={styleB} className="w-full h-full object-cover object-top" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-2 left-2 right-2">
                  <p className="text-white text-xs font-bold truncate">{styleB}</p>
                  <p className="text-white/70 text-xs">{getStyleData(styleB)!.category}</p>
                </div>
                <button
                  onClick={() => handleSelectAsMain(styleB)}
                  className="absolute top-2 right-2 px-2 py-1 bg-teal-500 text-white text-xs font-semibold rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Use This
                </button>
              </div>
            </div>

            {/* VS badge */}
            <div className="flex items-center justify-center py-2 gap-3">
              <span className={`text-xs font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{styleA}</span>
              <span className={`px-3 py-1 rounded-full text-xs font-black ${darkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700'}`}>VS</span>
              <span className={`text-xs font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{styleB}</span>
            </div>
          </div>
        )}

        {/* Prompt when not both selected */}
        {(!styleA || !styleB) && (
          <div className={`rounded-xl border-2 border-dashed p-4 text-center ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
            <i className={`ri-contrast-2-line text-2xl mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}></i>
            <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {!styleA && !styleB ? 'Select Style A and Style B below to compare' : !styleA ? 'Now pick Style A below' : 'Now pick Style B below'}
            </p>
          </div>
        )}

        {/* Two-column picker */}
        <div className="grid grid-cols-2 gap-3">
          {/* Style A Column */}
          <div>
            <div className={`flex items-center gap-2 mb-2 px-2 py-1.5 rounded-lg ${darkMode ? 'bg-teal-900/30' : 'bg-teal-50'}`}>
              <div className="w-5 h-5 rounded-full bg-teal-500 flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs font-bold">A</span>
              </div>
              <span className={`text-xs font-semibold ${darkMode ? 'text-teal-300' : 'text-teal-700'}`}>
                {styleA || 'Pick Style A'}
              </span>
            </div>
            <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
              {styleCategories.map((category) => (
                <div key={`a-${category.category}`}>
                  <p className={`text-xs font-semibold mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{category.category}</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {category.styles.map((style) => (
                      <button
                        key={`a-${style.name}`}
                        onClick={() => setStyleA(style.name)}
                        className={`relative rounded-lg overflow-hidden border-2 transition-all duration-200 cursor-pointer ${
                          styleA === style.name
                            ? 'border-teal-500 ring-1 ring-teal-500'
                            : darkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="w-full h-16">
                          <img src={style.image} alt={style.name} className="w-full h-full object-cover object-top" />
                        </div>
                        <div className={`px-1 py-1 text-center ${styleA === style.name ? (darkMode ? 'bg-teal-900/40' : 'bg-teal-50') : darkMode ? 'bg-gray-700' : 'bg-white'}`}>
                          <span className={`text-xs font-medium leading-tight block ${styleA === style.name ? 'text-teal-500' : darkMode ? 'text-gray-300' : 'text-gray-800'}`}>
                            {style.name}
                          </span>
                        </div>
                        {styleA === style.name && (
                          <div className="absolute top-1 right-1 w-4 h-4 bg-teal-500 rounded-full flex items-center justify-center">
                            <i className="ri-check-line text-white text-xs"></i>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Style B Column */}
          <div>
            <div className={`flex items-center gap-2 mb-2 px-2 py-1.5 rounded-lg ${darkMode ? 'bg-orange-900/30' : 'bg-orange-50'}`}>
              <div className="w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs font-bold">B</span>
              </div>
              <span className={`text-xs font-semibold ${darkMode ? 'text-orange-300' : 'text-orange-700'}`}>
                {styleB || 'Pick Style B'}
              </span>
            </div>
            <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
              {styleCategories.map((category) => (
                <div key={`b-${category.category}`}>
                  <p className={`text-xs font-semibold mb-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{category.category}</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {category.styles.map((style) => (
                      <button
                        key={`b-${style.name}`}
                        onClick={() => setStyleB(style.name)}
                        className={`relative rounded-lg overflow-hidden border-2 transition-all duration-200 cursor-pointer ${
                          styleB === style.name
                            ? 'border-orange-500 ring-1 ring-orange-500'
                            : darkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="w-full h-16">
                          <img src={style.image} alt={style.name} className="w-full h-full object-cover object-top" />
                        </div>
                        <div className={`px-1 py-1 text-center ${styleB === style.name ? (darkMode ? 'bg-orange-900/40' : 'bg-orange-50') : darkMode ? 'bg-gray-700' : 'bg-white'}`}>
                          <span className={`text-xs font-medium leading-tight block ${styleB === style.name ? 'text-orange-500' : darkMode ? 'text-gray-300' : 'text-gray-800'}`}>
                            {style.name}
                          </span>
                        </div>
                        {styleB === style.name && (
                          <div className="absolute top-1 right-1 w-4 h-4 bg-orange-500 rounded-full flex items-center justify-center">
                            <i className="ri-check-line text-white text-xs"></i>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header with Compare and Favorites Toggle */}
      <div className="flex items-center justify-between gap-3">
        <h3 className={`text-lg font-semibold transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
          Select Style
        </h3>
        <div className="flex items-center gap-2">
          {/* Favorites Toggle */}
          <button
            onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 whitespace-nowrap ${
              showFavoritesOnly
                ? 'bg-teal-500 text-white hover:bg-teal-600'
                : darkMode
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <i className={`${showFavoritesOnly ? 'ri-heart-fill' : 'ri-heart-line'} mr-1`}></i>
            Favorites {favorites.length > 0 && `(${favorites.length})`}
          </button>

          {/* Compare Button */}
          <button
            onClick={() => setCompareMode(true)}
            className="px-4 py-2 bg-teal-500 text-white rounded-lg text-sm font-medium hover:bg-teal-600 transition-colors duration-300 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-contrast-2-line mr-1"></i>
            Compare Styles
          </button>
        </div>
      </div>

      {/* Recent Styles Section */}
      {styleHistory.length > 0 && (
        <div className={`rounded-xl border p-4 transition-colors duration-300 ${
          darkMode ? 'border-gray-600 bg-gray-700/30' : 'border-gray-200 bg-gray-50'
        }`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <i className={`ri-history-line text-base ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}></i>
              <h4 className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Recent Styles
              </h4>
            </div>
            <button
              onClick={clearHistory}
              className={`text-xs font-medium transition-colors duration-300 hover:underline cursor-pointer ${
                darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Clear History
            </button>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {styleHistory.map((item) => (
              <div
                key={item.name}
                className={`relative flex-shrink-0 w-32 rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                  selectedStyle === item.name
                    ? 'border-teal-500 ring-2 ring-teal-500'
                    : darkMode
                    ? 'border-gray-600'
                    : 'border-gray-300'
                }`}
              >
                <button
                  onClick={(e) => removeFromHistory(item.name, e)}
                  className={`absolute top-1 right-1 z-10 w-5 h-5 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer ${
                    darkMode
                      ? 'bg-gray-800/80 text-gray-400 hover:bg-red-500 hover:text-white'
                      : 'bg-white/80 text-gray-500 hover:bg-red-500 hover:text-white'
                  }`}
                >
                  <i className="ri-close-line text-xs"></i>
                </button>
                <button
                  onClick={() => handleSelectStyle(item.name)}
                  className="w-full cursor-pointer"
                >
                  <div className="w-full h-20">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className={`px-2 py-1.5 text-center transition-colors duration-300 ${
                    selectedStyle === item.name
                      ? 'bg-teal-50 dark:bg-teal-900/20'
                      : darkMode
                      ? 'bg-gray-700'
                      : 'bg-white'
                  }`}>
                    <span className={`text-xs font-medium block truncate transition-colors duration-300 ${
                      selectedStyle === item.name
                        ? 'text-teal-600 dark:text-teal-400'
                        : darkMode
                        ? 'text-gray-300'
                        : 'text-gray-800'
                    }`}>
                      {item.name}
                    </span>
                    <span className={`text-xs block truncate ${darkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                      {item.category}
                    </span>
                  </div>
                </button>
                {selectedStyle === item.name && (
                  <div className="absolute top-1 left-1 w-5 h-5 bg-teal-500 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-white text-xs"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State for Favorites */}
      {showFavoritesOnly && favorites.length === 0 && (
        <div className={`text-center py-12 rounded-lg border-2 border-dashed transition-colors duration-300 ${
          darkMode ? 'border-gray-600 bg-gray-800/30' : 'border-gray-300 bg-gray-50'
        }`}>
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <i className={`ri-heart-line text-5xl transition-colors duration-300 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}></i>
          </div>
          <h4 className={`text-base font-semibold mb-2 transition-colors duration-300 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            No favorites yet
          </h4>
          <p className={`text-sm transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Click <i className="ri-heart-line"></i> on any style to save it
          </p>
        </div>
      )}

      {/* Style Grid */}
      {filteredCategories.map((category) => (
        <div key={category.category}>
          <h3 className={`text-sm font-semibold mb-3 transition-colors duration-300 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {category.category}
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {category.styles.map((style) => {
              const isStyleFavorite = isFavorite(style.name);
              const currentPalette = getCurrentPalette(style.name);
              const isPaletteExpanded = expandedPalette === style.name;
              
              return (
                <div key={style.name} className="space-y-2">
                  <button
                    onClick={() => handleSelectStyle(style.name)}
                    className={`relative rounded-lg overflow-hidden border-2 transition-all duration-300 cursor-pointer w-full ${
                      selectedStyle === style.name
                        ? 'border-teal-500 ring-2 ring-teal-500 ring-offset-2 dark:ring-offset-gray-800'
                        : isStyleFavorite
                        ? darkMode
                          ? 'border-teal-500/50 hover:border-teal-500'
                          : 'border-teal-400/50 hover:border-teal-400'
                        : darkMode
                        ? 'border-gray-600 hover:border-gray-500'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {/* Favorite Toggle Button — always visible */}
                    <button
                      onClick={(e) => toggleFavorite(style.name, e)}
                      title={isStyleFavorite ? 'Remove from favorites' : 'Add to favorites'}
                      className={`absolute top-2 left-2 z-10 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 cursor-pointer shadow-md ${
                        isStyleFavorite
                          ? 'bg-rose-500 text-white hover:bg-rose-600 scale-110'
                          : darkMode
                          ? 'bg-gray-900/70 text-gray-300 hover:bg-rose-500 hover:text-white'
                          : 'bg-white/90 text-gray-400 hover:bg-rose-500 hover:text-white'
                      }`}
                    >
                      <i className={`${isStyleFavorite ? 'ri-heart-fill' : 'ri-heart-line'} text-sm`}></i>
                    </button>

                    <div className="w-full h-32">
                      <img
                        src={style.image}
                        alt={style.name}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className={`p-2 transition-colors duration-300 ${
                      selectedStyle === style.name
                        ? 'bg-teal-50 dark:bg-teal-900/20'
                        : darkMode
                        ? 'bg-gray-700'
                        : 'bg-white'
                    }`}>
                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-medium transition-colors duration-300 ${
                          selectedStyle === style.name
                            ? 'text-teal-600 dark:text-teal-400'
                            : darkMode
                            ? 'text-gray-300'
                            : 'text-gray-900'
                        }`}>
                          {style.name}
                        </span>
                        {isStyleFavorite && (
                          <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${darkMode ? 'bg-rose-900/40 text-rose-400' : 'bg-rose-50 text-rose-500'}`}>
                            ♥ Saved
                          </span>
                        )}
                      </div>
                      
                      {/* Color Palette Strip Badge */}
                      <div className="flex items-center gap-1 mt-2">
                        <div className="w-5 h-5 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: currentPalette.wall }} title="Wall"></div>
                        <div className="w-5 h-5 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: currentPalette.roof }} title="Roof"></div>
                        <div className="w-5 h-5 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: currentPalette.trim }} title="Trim"></div>
                      </div>
                    </div>
                    {selectedStyle === style.name && (
                      <div className="absolute top-2 right-2 w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                    )}
                  </button>

                  {/* Customize Colors Section */}
                  {selectedStyle === style.name && (
                    <div className={`rounded-lg border transition-all duration-300 overflow-hidden ${
                      darkMode ? 'border-gray-600 bg-gray-700/50' : 'border-gray-200 bg-gray-50'
                    }`}>
                      <button
                        onClick={() => setExpandedPalette(isPaletteExpanded ? null : style.name)}
                        className={`w-full px-3 py-2 flex items-center justify-between transition-colors duration-300 cursor-pointer ${
                          darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <i className={`ri-palette-line text-sm ${darkMode ? 'text-teal-400' : 'text-teal-600'}`}></i>
                          <span className={`text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Customize Colors
                          </span>
                        </div>
                        <i className={`ri-arrow-${isPaletteExpanded ? 'up' : 'down'}-s-line text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}></i>
                      </button>

                      {isPaletteExpanded && (
                        <div className="px-3 pb-3 space-y-3">
                          {/* Preset Palettes */}
                          {predefinedPalettes[style.name] && (
                            <div>
                              <label className={`block text-xs font-medium mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                Preset Palettes
                              </label>
                              <div className="flex gap-2">
                                {predefinedPalettes[style.name].map((palette, idx) => (
                                  <button
                                    key={idx}
                                    onClick={() => handlePresetPaletteSelect(style.name, palette)}
                                    className={`flex-1 h-8 rounded-lg border-2 transition-all duration-300 cursor-pointer overflow-hidden ${
                                      JSON.stringify(currentPalette) === JSON.stringify(palette)
                                        ? 'border-teal-500 ring-2 ring-teal-500'
                                        : darkMode
                                        ? 'border-gray-600 hover:border-gray-500'
                                        : 'border-gray-300 hover:border-gray-200'
                                    }`}
                                    title={`Preset ${idx + 1}`}
                                  >
                                    <div className="flex h-full">
                                      <div className="flex-1" style={{ backgroundColor: palette.wall }}></div>
                                      <div className="flex-1" style={{ backgroundColor: palette.roof }}></div>
                                      <div className="flex-1" style={{ backgroundColor: palette.trim }}></div>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Custom Color Pickers */}
                          <div className="space-y-2">
                            <label className={`block text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              Custom Colors
                            </label>
                            
                            {/* Exterior Wall */}
                            <div className="flex items-center gap-2">
                              <input
                                type="color"
                                value={currentPalette.wall}
                                onChange={(e) => handleCustomColorChange(style.name, 'wall', e.target.value)}
                                className="w-10 h-10 rounded-lg border-2 border-gray-300 dark:border-gray-600 cursor-pointer"
                              />
                              <div className="flex-1">
                                <div className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                  Exterior Wall
                                </div>
                                <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                  {currentPalette.wall.toUpperCase()}
                                </div>
                              </div>
                            </div>

                            {/* Roof */}
                            <div className="flex items-center gap-2">
                              <input
                                type="color"
                                value={currentPalette.roof}
                                onChange={(e) => handleCustomColorChange(style.name, 'roof', e.target.value)}
                                className="w-10 h-10 rounded-lg border-2 border-gray-300 dark:border-gray-600 cursor-pointer"
                              />
                              <div className="flex-1">
                                <div className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                  Roof
                                </div>
                                <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                  {currentPalette.roof.toUpperCase()}
                                </div>
                              </div>
                            </div>

                            {/* Trim/Accent */}
                            <div className="flex items-center gap-2">
                              <input
                                type="color"
                                value={currentPalette.trim}
                                onChange={(e) => handleCustomColorChange(style.name, 'trim', e.target.value)}
                                className="w-10 h-10 rounded-lg border-2 border-gray-300 dark:border-gray-600 cursor-pointer"
                              />
                              <div className="flex-1">
                                <div className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                  Trim/Accent
                                </div>
                                <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                  {currentPalette.trim.toUpperCase()}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}